/**
 * Chutt... - Trend Prediction Card (Radar V2)
 * Carte affichant une prediction de tendance avec trajectoire,
 * probabilite de crise, score, plateformes et action recommandee.
 */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  LinearProgress,
  Collapse,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import { colors } from '../../styles/colors';

/**
 * Trajectory badge configuration.
 */
const trajectoryConfig = {
  rising: {
    label: 'En hausse',
    color: colors.warning,
    bgColor: colors.warningBg,
    icon: TrendingUpIcon,
  },
  falling: {
    label: 'En baisse',
    color: colors.success,
    bgColor: colors.successBg,
    icon: TrendingDownIcon,
  },
  stable: {
    label: 'Stable',
    color: colors.gray500,
    bgColor: 'rgba(107, 114, 128, 0.1)',
    icon: TrendingFlatIcon,
  },
  spike: {
    label: 'Pic detecte',
    color: colors.danger,
    bgColor: colors.dangerBg,
    icon: WhatshotIcon,
  },
};

/**
 * Returns the color for a crisis probability value (0-100).
 */
function getCrisisColor(probability) {
  if (probability >= 75) return colors.danger;
  if (probability >= 50) return colors.warning;
  if (probability >= 25) return colors.warningLight;
  return colors.success;
}

function TrendPredictionCard({ prediction }) {
  const [expanded, setExpanded] = useState(false);

  if (!prediction) return null;

  const {
    topic,
    predicted_trajectory,
    crisis_probability = 0,
    prediction_score = 0,
    sentiment_momentum = 0,
    platforms = [],
    recommended_preemptive_action,
  } = prediction;

  const trajectory = trajectoryConfig[predicted_trajectory] || trajectoryConfig.stable;
  const TrajectoryIcon = trajectory.icon;
  const crisisColor = getCrisisColor(crisis_probability);

  const momentumPositive = sentiment_momentum > 0;
  const momentumColor = momentumPositive ? colors.positive : sentiment_momentum < 0 ? colors.negative : colors.gray400;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: `1px solid ${colors.gray200}`,
        bgcolor: colors.white,
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          borderColor: colors.gray300,
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
        },
      }}
    >
      {/* Header: topic + trajectory badge */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '0.95rem',
            color: colors.charcoal,
            lineHeight: 1.3,
            flex: 1,
            mr: 1,
          }}
        >
          {topic}
        </Typography>
        <Chip
          icon={<TrajectoryIcon sx={{ fontSize: '14px !important' }} />}
          label={trajectory.label}
          size="small"
          sx={{
            height: 24,
            fontSize: '0.7rem',
            fontWeight: 600,
            bgcolor: trajectory.bgColor,
            color: trajectory.color,
            border: 'none',
            '& .MuiChip-icon': {
              color: 'inherit',
            },
          }}
        />
      </Box>

      {/* Crisis probability bar */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="caption" sx={{ color: colors.gray500, fontWeight: 500, fontSize: '0.72rem' }}>
            Probabilite de crise
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: crisisColor,
              fontWeight: 700,
              fontSize: '0.75rem',
            }}
          >
            {crisis_probability}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={crisis_probability}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: 'rgba(0, 0, 0, 0.04)',
            '& .MuiLinearProgress-bar': {
              borderRadius: 3,
              bgcolor: crisisColor,
              transition: 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
            },
          }}
        />
      </Box>

      {/* Metrics row: prediction score + sentiment momentum */}
      <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
        <Box>
          <Typography variant="caption" sx={{ color: colors.gray500, fontSize: '0.7rem', fontWeight: 500 }}>
            Score prediction
          </Typography>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: colors.charcoal }}>
            {prediction_score}
            <Typography
              component="span"
              sx={{ fontSize: '0.7rem', color: colors.gray400, ml: 0.3, fontWeight: 400 }}
            >
              / 100
            </Typography>
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: colors.gray500, fontSize: '0.7rem', fontWeight: 500 }}>
            Momentum sentiment
          </Typography>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: momentumColor }}>
            {momentumPositive ? '+' : ''}{sentiment_momentum.toFixed(1)}
          </Typography>
        </Box>
      </Box>

      {/* Platform chips */}
      {platforms.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
          {platforms.map((platform) => (
            <Chip
              key={platform}
              label={platform}
              size="small"
              sx={{
                height: 22,
                fontSize: '0.65rem',
                fontWeight: 500,
                bgcolor: colors.gray100,
                color: colors.gray600,
                border: 'none',
              }}
            />
          ))}
        </Box>
      )}

      {/* Recommended action (collapsible) */}
      {recommended_preemptive_action && (
        <Box
          sx={{
            borderTop: `1px solid ${colors.gray100}`,
            pt: 1,
            mt: 0.5,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              userSelect: 'none',
            }}
            onClick={() => setExpanded(!expanded)}
          >
            <Typography
              variant="caption"
              sx={{
                color: colors.accent,
                fontWeight: 600,
                fontSize: '0.72rem',
              }}
            >
              Action recommandee
            </Typography>
            <IconButton
              size="small"
              aria-label="Developper les details"
              sx={{
                p: 0.3,
                transition: 'transform 0.2s ease',
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                color: colors.gray400,
              }}
            >
              <ExpandMoreIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
          <Collapse in={expanded}>
            <Box
              sx={{
                mt: 1,
                p: 1.5,
                borderRadius: 1.5,
                bgcolor: colors.accentBg,
                border: `1px solid rgba(46, 158, 107, 0.12)`,
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.8rem',
                  color: colors.gray700,
                  lineHeight: 1.5,
                }}
              >
                {recommended_preemptive_action}
              </Typography>
            </Box>
          </Collapse>
        </Box>
      )}
    </Paper>
  );
}

export default TrendPredictionCard;
